import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface CopilotMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  toolCalls?: CopilotToolCall[];
  timestamp: Date;
}

export interface CopilotToolCall {
  name: string;
  args: Record<string, any>;
  status: "pending_confirmation" | "completed" | "failed" | "reading";
  result?: any;
}

export interface CopilotTenantContext {
  companyId: string;
  companyName: string;
}

export interface PendingAction {
  messageId: string;
  toolName: string;
  args: Record<string, any>;
  description: string;
}

export function useCopilot() {
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tenantContext, setTenantContext] = useState<CopilotTenantContext | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const sendMessage = useCallback(
    async (content: string) => {
      const userMessage: CopilotMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error("Not authenticated");
        }

        // Build message history for AI
        const aiMessages = [...messages, userMessage].map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/platform-admin-copilot`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              messages: aiMessages,
              tenantContext,
            }),
          }
        );

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error ?? `Request failed: ${response.status}`);
        }

        const data = await response.json();

        // Parse tool calls from the response
        const toolCalls: CopilotToolCall[] = [];
        if (data.tool_calls?.length) {
          for (const tc of data.tool_calls) {
            const args = JSON.parse(tc.function?.arguments ?? "{}");
            toolCalls.push({
              name: tc.function?.name ?? "unknown",
              args,
              status: args.confirmed === false ? "pending_confirmation" : "completed",
            });
          }
        }

        // Check for pending confirmations in the message content
        const assistantMessage: CopilotMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: data.message ?? "I wasn't able to generate a response.",
          toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: any) {
        const rawMsg = err.message?.toLowerCase() ?? "";
        const isNetworkErr =
          rawMsg.includes("dns") || rawMsg.includes("fetch") || rawMsg.includes("network") ||
          rawMsg.includes("enotfound") || rawMsg.includes("connectivity") ||
          rawMsg.includes("name or service") || rawMsg.includes("failed to fetch");

        const displayContent = isNetworkErr
          ? "Copilot is temporarily unavailable. Please try again in a moment."
          : `Error: ${err.message}`;

        const errorMessage: CopilotMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: displayContent,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [messages, tenantContext]
  );

  const confirmAction = useCallback(
    async (action: PendingAction) => {
      setPendingAction(null);
      // Send a confirmation message
      await sendMessage(
        `Yes, please execute: ${action.toolName} with args: ${JSON.stringify(action.args)}`
      );
    },
    [sendMessage]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setPendingAction(null);
  }, []);

  return {
    messages,
    isLoading,
    tenantContext,
    pendingAction,
    sendMessage,
    confirmAction,
    setTenantContext,
    setPendingAction,
    clearMessages,
  };
}
